import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { PureComponent } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, reduxForm, SubmissionError, type Validator } from "redux-form";
import { ActionButtonIntent, type ActionButtonProps } from "widgets";

import { type ISelectOption } from "@/app-utils/form.ts";
import { type InputUserAccountInput, type License, type Role } from "@/graphql.ts";
import { acceptableNewUserName, addUser, noMoreFreeSeatsText } from "@/redux/modules/users-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { SelForm } from "@/ui-modules/core/form/layout/Form.js";

import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import { AddNewUserDialogImpl } from "./AddNewUserDialog.js";
import UserEditingFields from "./UserEditingFields.js";

interface IAddNewUserToDatabaseFormOwnProps {
  roles: Role[];
  updateDialogState: (IAddNewUserDialogState) => void;
  numberOfFreeSeats: number;
  canCreateOracleUser: boolean;
  authenticationMode: string;
  onClose: () => void;
  refreshListOfUsers: () => void;
}

interface IAddNewUserToDatabaseFormDispatchProps {
  createUser: (userInput: InputUserAccountInput) => Promise<boolean>;
  acceptableNewUserName: (username: string) => Promise<boolean>;
}

interface IAddNewUserToDatabaseFormAppStateProps {
  initialValues: Partial<any>;
  license: License;
}

export interface IAddNewUserToDatabaseFormProps
  extends IAddNewUserToDatabaseFormAppStateProps,
    IAddNewUserToDatabaseFormDispatchProps,
    IAddNewUserToDatabaseFormOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(state: IAppState): IAddNewUserToDatabaseFormAppStateProps {
  return {
    license: state.config.configuration?.license,
    initialValues: {
      [AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_STATUS]: AddNewUserDialogImpl.statusOptions[0],
    },
  };
}

function mapDispatchToProps(dispatch): IAddNewUserToDatabaseFormDispatchProps {
  return {
    createUser: (userInput: InputUserAccountInput) => dispatch(addUser(userInput)),
    acceptableNewUserName: (username: string) => dispatch(acceptableNewUserName(username)),
  };
}

export const userNameFieldValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
  if (!value || value.trim().length === 0) {
    return `Your input is required.`;
  }
  const v = value.trim();
  if (/^[_0-9]/.exec(v)) {
    return "Underscore and digits are not allowed as the first character.";
  }
  if (v.length < 2) {
    return "At least 2 characters are required.";
  }
  if (/[_]$/.exec(v)) {
    return "Underscore is  not allowed as the last character.";
  }
  if (v.length > DB_FIELD_MAX_LENGTH.USER.ORACLE_USERID) {
    return `At most ${DB_FIELD_MAX_LENGTH.USER.ORACLE_USERID} characters are allowed.`;
  }
  if (!/^[a-zA-Z][a-zA-Z0-9_]*[a-zA-Z0-9]$/.exec(v)) {
    return AddNewUserToDatabaseFormImpl.idTooltip;
  }
  return undefined;
};

class AddNewUserToDatabaseFormImpl extends PureComponent<IAddNewUserToDatabaseFormProps> {
  public static ADD_USER_TO_DATABASE_FORM_NAME = "addUserToDatabaseForm";
  // field names
  public static ADD_USER_TO_DATABASE_FORM_FIELD_USERNAME = "userName";
  public static ADD_USER_TO_DATABASE_FORM_FIELD_FIRSTNAME = "firsName";
  public static ADD_USER_TO_DATABASE_FORM_FIELD_LASTNAME = "lastName";
  public static ADD_USER_TO_DATABASE_FORM_FIELD_EMAIL = "emailAddress";
  public static ADD_USER_TO_DATABASE_FORM_FIELD_ROLES = "roles";
  public static ADD_USER_TO_DATABASE_FORM_FIELD_STATUS = "accountStatus";

  public static idTooltip: string =
    `The user id must consist of at most ${DB_FIELD_MAX_LENGTH.USER.ORACLE_USERID} alphanumeric characters and underscore. ` +
    "Digits and underscore must not appear in the first and underscore not in the last position." +
    `The pattern is [a-zA-Z][a-zA-Z0-9_]*[a-zA-Z0-9]`;

  private wannaClose = false; // allows to discriminate between the 2 buttons pushed for submission, so that we know which one to "spin"

  constructor(props: IAddNewUserToDatabaseFormProps) {
    super(props);
  }

  initButtonProps = () => {
    this.props.updateDialogState({
      nextButtonProps: this.buildCreateAndCloseButtonProps(),
      otherButtonProps: this.buildCreateAndAddAnotherButtonProps(),
    });
  };

  componentDidMount() {
    this.initButtonProps();
  }

  handleSubmitDataAndClose = (values): Promise<void> => {
    this.wannaClose = true;
    return this.handleSubmitData(values).then(() => {
      this.props.refreshListOfUsers();
      this.props.onClose();
    });
  };

  handleSubmitDataAndAddAnother = (values): Promise<void> => {
    this.wannaClose = false;
    return this.handleSubmitData(values).then(() => {
      this.initButtonProps();
      this.props.reset();
      this.props.refreshListOfUsers();
    });
  };

  componentDidUpdate(prevProps: IAddNewUserToDatabaseFormProps) {
    if (
      this.props.asyncValidating !== prevProps.asyncValidating ||
      this.props.submitting !== prevProps.submitting ||
      //|| this.props.invalid !== prevProps.invalid
      this.props.error !== prevProps.error
    ) {
      this.props.updateDialogState({
        nextButtonProps: this.buildCreateAndCloseButtonProps(),
        otherButtonProps: this.buildCreateAndAddAnotherButtonProps(),
        error: this.props.error,
      });
    }
  }

  handleSubmitData = (values): Promise<boolean> => {
    const selectedRoleIds: number[] = values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_ROLES].map(
      (role: Role) => role.id,
    );
    const selectedStatus: ISelectOption = values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_STATUS];

    const payload: InputUserAccountInput = {
      userId: undefined,
      username: values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_USERNAME],
      firstName: values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_FIRSTNAME],
      lastName: values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_LASTNAME],
      email: values[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_EMAIL],
      roles: selectedRoleIds,
      isActive: selectedStatus && selectedStatus.value === 1,
      authenticationMode: this.props.authenticationMode,
    };

    return this.props.createUser(payload).then(
      (user: any) => user,
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  buildCreateAndAddAnotherButtonProps = (): ActionButtonProps => {
    // the case where there is not seat left should in theory never happen because of disabled buttons
    const onlyOneSeatLeft: boolean = this.props.numberOfFreeSeats === 0 || this.props.numberOfFreeSeats === 1;
    return {
      label: "Create and Add Another",
      isLoading: !this.wannaClose && this.props.submitting,
      isDisabled: this.props.submitting || onlyOneSeatLeft || !this.props.canCreateOracleUser,
      tooltip: onlyOneSeatLeft
        ? "There is only one free seat left"
        : "Create user and keep the dialog open to add another user",
      onPress: this.props.handleSubmit(this.handleSubmitDataAndAddAnother),
      intent: ActionButtonIntent.Outlined,
    };
  };

  buildCreateAndCloseButtonProps = (): ActionButtonProps => {
    const noMoreFreeSeats: boolean = this.props.numberOfFreeSeats === 0;
    return {
      label: "Create and Close",
      isLoading: this.wannaClose && this.props.submitting,
      isDisabled: this.props.submitting || noMoreFreeSeats || !this.props.canCreateOracleUser,
      tooltip: noMoreFreeSeats ? noMoreFreeSeatsText() : "Create user and close the dialog",
      onPress: this.props.handleSubmit(this.handleSubmitDataAndClose),
    };
  };

  missingOracleUserCreateGrantPlaceholderText = () => {
    return (
      <span>
        <p>This system is not configured to allow creation of new Oracle users.</p>
        <br />
        <p>If you wish to create users from here, please contact Genedata support for assistance.</p>
      </span>
    );
  };

  render() {
    return !this.props.canCreateOracleUser ? (
      <ContentPlaceholder text={this.missingOracleUserCreateGrantPlaceholderText()} />
    ) : (
      <SelForm>
        <InputReduxFormField
          componentClass="input"
          name={AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_USERNAME}
          label={"User ID"}
          placeholder={`Enter a unique ID for the account`}
          maxLength={DB_FIELD_MAX_LENGTH.USER.ORACLE_USERID}
          component={InputField}
          validate={userNameFieldValidation}
          tooltip={AddNewUserToDatabaseFormImpl.idTooltip}
        />

        <UserEditingFields roles={this.props.roles} isCreateContext={true} />
      </SelForm>
    );
  }

  static async asyncValidateUsername(values, _dispatch, props: IAddNewUserToDatabaseFormProps) {
    const username = values?.[AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_USERNAME];

    try {
      if (!(await props.acceptableNewUserName(username))) {
        throw `${username} either already exists in the database or is reserved for Oracle internal use.`;
      }
    } catch (e) {
      if (typeof e === "string") {
        throw {
          [AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_USERNAME]: e,
        };
      } else {
        // the rejection can be a genuine validation error or an unexpected exception
        throw e.message ? { _error: e.message } : e;
      }
    }
  }
}

const AddNewUserToDatabaseForm = connect<
  IAddNewUserToDatabaseFormAppStateProps,
  IAddNewUserToDatabaseFormDispatchProps,
  IAddNewUserToDatabaseFormOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_NAME,
    asyncValidate: AddNewUserToDatabaseFormImpl.asyncValidateUsername,
    asyncBlurFields: [AddNewUserToDatabaseFormImpl.ADD_USER_TO_DATABASE_FORM_FIELD_USERNAME], // so that only blurring this field triggers the async validation
  })(AddNewUserToDatabaseFormImpl) as any,
);

export { AddNewUserToDatabaseFormImpl };
export default AddNewUserToDatabaseForm;
